import http from "@/shared/services/api-client";
import type User from "@/shared/datamodels/user";
import {
  BadRequestError,
  NotFoundError,
  UnauthorizedError,
  UnexpectedError,
} from "@/shared/utils/custom-errors";
import {
  adminV1EmailCheckCreate,
  adminV1LoginCreate,
  adminV1LoginMagicLinkCreate,
  adminV1MagicLinkGenerateCreate,
} from "@/api/admin-auth/admin-auth";
import { adminV1ResetPasswordCreate } from "@/api/admin-auth/admin-auth";
import type {
  AccessToken,
  AdminEmailCheckResponse,
  AdminTokenObtainPair,
  MagicLinkAuthenticate,
} from "@/api/model";

import type { ApiError } from "@/types";

export const loginWithEmail = async (
  email: string,
  password: string
): Promise<AdminTokenObtainPair | ApiError> => {
  try {
    const result = await adminV1LoginCreate({
      email: email.toLowerCase().trim(),
      password: password,
    } as AdminTokenObtainPair);

    return result as unknown as AdminTokenObtainPair;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const loginWithToken = async (token: MagicLinkAuthenticate) => {
  try {
    const result = await adminV1LoginMagicLinkCreate(token);

    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const checkEmail = async (
  email: string
): Promise<AdminEmailCheckResponse | ApiError> => {
  try {
    const result = await adminV1EmailCheckCreate({ email });

    return result;
  } catch (e: any) {
    return { error: e.data };
  }
};

export const sendMagicLinkEmail = async (
  email: string
): Promise<void | ApiError> => {
  try {
    const result = await adminV1MagicLinkGenerateCreate({ email });

    return result;
  } catch (e: any) {
    return { error: e?.data?.detail };
  }
};

export const fetchCurrentUser = async (): Promise<User> => {
  const res = await http.get("/api/admin/v1/profiles/me/");
  if (res?.status == 401) throw new UnauthorizedError();
  if (res?.status != 200) throw new UnexpectedError();
  try {
    return {
      id: res?.data?.id,
      uuid: res?.data?.uuid,
      email: res?.data?.user?.email,
      is_coach: res?.data?.is_coach,
      is_tfa_employee: res?.data?.user?.is_tfa_employee,
      is_admin: res?.data?.is_admin,
      can_manage_payments: res?.data?.can_manage_payments,
      services_instructor_id: res?.data?.services_instructor_id ?? null,
      name: res?.data?.name,
      level_of_play: res?.data?.level_of_play,
      // Only used to decide whether to offer the org picker. The picker itself
      // re-fetches the authoritative, selectable list from /api/v2/me/organizations/,
      // which — unlike organization_profiles — excludes archived profiles.
      admin_organization_ids: (res?.data?.organization_profiles ?? [])
        .filter((profile: { is_admin?: boolean }) => profile?.is_admin)
        .map((profile: { organization_id: number }) => profile.organization_id),
      default_organization: {
        id: res?.data?.organization?.id,
        name: res?.data?.organization?.name,
        organization_type: res?.data?.organization?.organization_type,
        payment_links: res?.data?.organization?.payment_links,
        timezone: res?.data?.organization?.timezone,
      },
    } as User;
  } catch (e) {
    throw new UnexpectedError();
  }
};

export interface AdminOrganizationOption {
  organization_id: number;
  organization_name: string;
  is_admin: boolean;
  is_coach: boolean;
  profile_id: number;
  name: string;
  picture_url: string | null;
}

/**
 * The organizations the user can run the admin as — one entry per active admin
 * profile. Non-admin memberships are dropped: selecting one returns a token
 * without the `admin` role, which every /api/admin/ endpoint rejects.
 */
export const fetchAdminOrganizations = async (): Promise<
  AdminOrganizationOption[]
> => {
  const res = await http.get("/api/v2/me/organizations/");
  if (res?.status != 200) throw new UnexpectedError();
  const memberships: AdminOrganizationOption[] = res?.data ?? [];
  return memberships.filter((membership) => membership.is_admin);
};

/** Swap the session for one scoped to `organizationId`, returning fresh tokens. */
export const selectOrganization = async (
  organizationId: number
): Promise<AccessToken> => {
  const res = await http.post("/api/v2/select-organization/", {
    organization_id: organizationId,
  });
  if (res?.status != 200) throw new UnexpectedError();
  return res.data as AccessToken;
};

export const forgotPassword = async (
  email: string
): Promise<void | ApiError> => {
  try {
    const result = await adminV1ResetPasswordCreate({
      email: email.toLowerCase().trim(),
    });
    return result;
  } catch (e: any) {
    return { error: e?.message };
  }
};

export const changePassword = async (
  code: string,
  new_password: string
): Promise<boolean> => {
  const res = await http.post("/api/v2/change-password/", {
    code,
    new_password,
  });
  const message = "Invalid confirmation code.";
  if (res?.status == 404) throw new NotFoundError(message);
  if (res?.status == 400) throw new BadRequestError(message);
  if (res?.status != 200) throw new UnexpectedError();
  return res?.status == 200;
};
